import React, { useState, useEffect } from 'react';

/**
 * ProgressBar Component
 * 
 * A beautiful animated progress bar that shows the stages of mood processing
 * with smooth transitions and visual feedback for each stage.
 * 
 * @param {Array} stages - Array of stage objects with {id, label, description}
 * @param {number} currentStage - Current active stage index (0-based)
 * @param {boolean} isVisible - Whether to show the progress bar
 */
export default function ProgressBar({ stages, currentStage, isVisible }) {
  const [displayedStage, setDisplayedStage] = useState(0);

  // Reset displayedStage when progress bar becomes visible and starts from stage 0
  useEffect(() => {
    if (isVisible && currentStage === 0) {
      setDisplayedStage(0);
    }
  }, [isVisible, currentStage]);

  // Animate to the current stage with a slight delay for smooth transition
  useEffect(() => {
    if (currentStage > displayedStage) {
      const timer = setTimeout(() => {
        setDisplayedStage(currentStage);
      }, 300); // Small delay for smooth animation
      return () => clearTimeout(timer);
    }
  }, [currentStage, displayedStage]);

  if (!isVisible) return null;

  return (
    <div style={styles.container}>
      <div style={styles.progressContainer}>
        {/* Progress Line */}
        <div style={styles.progressLine}>
          <div 
            style={{
              ...styles.progressFill,
              width: `${stages.length > 1 ? (displayedStage / (stages.length - 1)) * 100 : 100}%`
            }}
          />
        </div>

        {/* Stage Indicators */}
        <div style={styles.stagesContainer}>
          {stages.map((stage, index) => (
            <div key={stage.id} style={styles.stageWrapper}>
              <div
                style={{
                  ...styles.stageIndicator,
                  ...(index <= displayedStage ? styles.stageCompleted : styles.stagePending),
                  ...(index === displayedStage ? styles.stageActive : {})
                }}
              >
                {index < displayedStage ? (
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                  </svg>
                ) : index === displayedStage ? (
                  <div style={styles.spinner}>
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M8 3a5 5 0 1 0 5 5H8V3z"/>
                    </svg>
                  </div>
                ) : (
                  <span style={styles.stageNumber}>{index + 1}</span>
                )}
              </div>
              
              {/* Stage Label */}
              <div style={styles.stageLabel}>
                <div style={{
                  ...styles.stageTitle,
                  ...(index <= displayedStage ? styles.stageTitleActive : {})
                }}>
                  {stage.label}
                </div>
                {index === displayedStage && (
                  <div style={styles.stageDescription}>
                    {stage.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

// Component styles
const styles = {
  container: {
    width: '100%',
    padding: '1.5rem',
    backgroundColor: '#f8fafc',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    marginTop: '1rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  progressContainer: {
    position: 'relative',
    marginBottom: '1rem',
  },
  progressLine: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    right: '20px',
    height: '4px',
    backgroundColor: '#e2e8f0',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
    borderRadius: '2px',
    transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 0 8px rgba(99, 102, 241, 0.4)',
  },
  stagesContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
    zIndex: 2,
  },
  stageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    maxWidth: '120px',
  },
  stageIndicator: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '0.5rem',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    zIndex: 3,
  },
  stageCompleted: {
    backgroundColor: '#10b981',
    color: 'white',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
  },
  stageActive: {
    backgroundColor: '#6366f1',
    color: 'white',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
    animation: 'pulse 2s infinite',
  },
  stagePending: {
    backgroundColor: '#e2e8f0',
    color: '#64748b',
    border: '2px solid #cbd5e1',
  },
  stageNumber: {
    fontSize: '14px',
    fontWeight: '600',
  },
  spinner: {
    animation: 'spin 1s linear infinite',
  },
  stageLabel: {
    textAlign: 'center',
    minHeight: '40px',
  },
  stageTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    marginBottom: '0.25rem',
    transition: 'color 0.3s ease',
  },
  stageTitleActive: {
    color: '#1e293b',
  },
  stageDescription: {
    fontSize: '10px',
    color: '#6366f1',
    fontWeight: '500',
    animation: 'fadeIn 0.5s ease-in-out',
  },
};

// Add CSS animations to global styles
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  @keyframes pulse {
    0%, 100% { 
      transform: scale(1);
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
    }
    50% { 
      transform: scale(1.05);
      box-shadow: 0 6px 16px rgba(99, 102, 241, 0.6);
    }
  }
  
  @keyframes fadeIn {
    from { 
      opacity: 0;
      transform: translateY(4px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
document.head.appendChild(styleSheet);
